import { FC, useContext, useEffect, useRef, useState } from "react";
import {
  Input,
  Select,
  Flex,
  Dropdown,
  Button,
  MenuProps,
  notification,
} from "antd";
import { PageId } from "@/types/page";
import {
  ApplicationTypeEnum,
  ApproveStatusEnum,
  ReimbursRequest,
} from "@/types/reimburse";
import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef,
} from "@/styles/STableLayout";
import useDataList from "@/hooks/useDataList";
import {
  myRequestList,
  myApproveList,
  allApproveList,
  applicationSubmit,
  moiGenOrder,
  templateGet,
} from "@/apis/reimburse";
import { CommonListParams } from "@/types/common";
import { useTranslation } from "react-i18next";
import { AnyObject } from "antd/es/_util/type";
import usePage from "@/layout/hooks/usePage";
import useReimburseColumns from "../hooks/useReimburseColumns";
import { CustomTabPaneContext } from "@/components/TabPane";
import AddReimbursement from "./AddReimbursement";
import AddDailySupplies from "./AddDailySupplies";
import useFrontDelTip from "@/hooks/useFrontDelTip";
import DetailAndApprove from "./DetailAndApprove";
import AddMaterialOverIssue from "./AddMaterialOverIssue";
import DetailMaterialOverIssue from "./DetailMaterialOverIssue";
import errorCodes from "@/apis/error";
import { applicationTypeMap } from "../utils/approveStatus";
import OverProductModal from "@/pages/Requirement/pages/MaterialOverIssueList/OverProductModal";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";

interface ReimburseFilters {
  keyword: string;
  status: ApproveStatusEnum | undefined;
}

const INITIAL_REIMBURSE_FILTERS: ReimburseFilters = {
  keyword: "",
  status: undefined,
};

const ReimburseList: FC = () => {
  const { t } = useTranslation();
  const { onOpen } = useContext(CustomTabPaneContext);

  const { pageId } = usePage();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<ReimburseFilters>(INITIAL_REIMBURSE_FILTERS);
  const { keyword, status } = draftFilters;
  const [addApplicationType, setAddApplicationType] = useState<
    ApplicationTypeEnum | undefined
  >(undefined);
  const [templateCheckingType, setTemplateCheckingType] = useState<
    ApplicationTypeEnum | undefined
  >(undefined);

  const [detail, setDetail] = useState<ReimbursRequest | null>(null);
  const [openProduct, setOpenProduct] = useState<string>('');


  const [list, total, listLoading, { fetchList, updateItem, addItem }] =
    useDataList<
      ReimbursRequest,
      [ApproveStatusEnum | undefined, CommonListParams<string>]
    >({
      fetchApi:
        pageId === PageId.REIMBURSE_MINE
          ? myRequestList
          : pageId === PageId.REIMBURSE_APPROVE
            ? myApproveList
            : pageId === PageId.REIMBURSE_ALL
              ? allApproveList
              : myRequestList,
    });

  const { onConfirm: onSubmit } = useFrontDelTip({
    cb: (reSubmit: boolean, id: string) =>
      applicationSubmit(reSubmit, id)
        .then((res) => {
          notification.success({
            placement: "top",
            message: t("reimburse.submitSuccess"),
          });
          updateItem(res);
        })
        .catch((err) => {
          notification.error({
            placement: "top",
            message: t("reimburse.submitFailed"),
          });
        }),
    message: t("common.tip"),
    desc: t("reimburse.submitConfirm"),
  });

  const handleDetail = (data: ReimbursRequest) => {
    setDetail(data);
  };

  const columns = useReimburseColumns({
    pageId,
    onDetail: handleDetail,
    onOpenTab: onOpen,
    onSubmit,
    onGenerateOrder: setOpenProduct,
  });

  const fetch = (
    offset: number,
    filters: ReimburseFilters,
  ) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    return fetchList(filters.status, {
      pagination: { offset, take },
      body: filters.keyword,
    });
  };

  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_REIMBURSE_FILTERS);
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
    // 三个申请审批入口共用组件；切换页面时统一重置筛选和页码。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, replaceFilters]);

  const handleSearch = () => {
    const nextFilters = commitFilters();
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_REIMBURSE_FILTERS);
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  // 状态选项
  const statusOptions = [
    { label: t("reimburse.statusDraft"), value: ApproveStatusEnum.DRAFT },
    { label: t("reimburse.statusPending"), value: ApproveStatusEnum.PENDING },
    { label: t("reimburse.statusApproved"), value: ApproveStatusEnum.APPROVED },
    { label: t("reimburse.statusRejected"), value: ApproveStatusEnum.REJECTED },
    { label: t("reimburse.statusReturned"), value: ApproveStatusEnum.RETURNED },
  ];

  const addMenuItems: MenuProps["items"] = [
    {
      key: ApplicationTypeEnum.REIMBURSEMENT,
      label: t(applicationTypeMap[ApplicationTypeEnum.REIMBURSEMENT].label),
    },
    {
      key: ApplicationTypeEnum.DAILY_SUPPLIES,
      label: t(applicationTypeMap[ApplicationTypeEnum.DAILY_SUPPLIES].label),
    },
    {
      key: ApplicationTypeEnum.MATERIAL_OVER_ISSUE,
      label: t(applicationTypeMap[ApplicationTypeEnum.MATERIAL_OVER_ISSUE].label),
    },
  ];

  const handleOpenAddApplication = async (
    applicationType: ApplicationTypeEnum,
  ) => {
    setTemplateCheckingType(applicationType);
    try {
      await templateGet(applicationType);
      setAddApplicationType(applicationType);
    } catch (error) {
      console.error("Approve template check error:", error);
      notification.error({
        placement: "top",
        message: t("common.tip"),
        description: `请先配置${t("layout.approveTemplate")}`,
      });
    } finally {
      setTemplateCheckingType(undefined);
    }
  };

  const headerChildren = (
    <Flex gap={8} style={{ width: "100%" }} justify="space-between">
      <Flex gap={8}>
        {pageId === PageId.REIMBURSE_MINE ? (
          <Dropdown
            menu={{
              items: addMenuItems,
              onClick: ({ key }) =>
                handleOpenAddApplication(Number(key) as ApplicationTypeEnum),
            }}
            trigger={["click"]}
          >
            <Button
              type="primary"
              loading={templateCheckingType !== undefined}
              disabled={templateCheckingType !== undefined}
            >
              {t("reimburse.addApplication")}
            </Button>
          </Dropdown>
        ) : (
          <div></div>
        )}
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={listLoading}
        >
          {({ submit }) => (
            <>
              <Select
                placeholder={t("reimburse.status")}
                options={statusOptions}
                allowClear
                style={{ width: 150 }}
                value={status}
                onChange={value => updateDraftFilters({ status: value })}
              />
              <Input
                placeholder={t("category.plSearch")}
                value={keyword}
                onChange={event => updateDraftFilters({ keyword: event.target.value })}
                onPressEnter={submit}
                style={{ width: 200 }}
              />
            </>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  const handleSuccess = (data: ReimbursRequest, type: "ADD" | "UPDATE") => {
    type === "ADD" && addItem(data);
    type === "UPDATE" && updateItem(data);
    setAddApplicationType(undefined);
    setDetail(null);
  };

  return (
    <>
      <STableLayout
        loading={listLoading}
        dataSource={list}
        rowKey="id"
        columns={columns as Array<AnyObject>}
        scrollX={1200}
        total={total}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        handleChange={offset => fetch(offset, appliedFilters)}
      />
      {
        !!openProduct && <OverProductModal open={!!openProduct} moiReimburseId={openProduct} onClose={() => setOpenProduct('')} onSucc={() => setOpenProduct('')} />
      }
      {addApplicationType === ApplicationTypeEnum.REIMBURSEMENT && (
        <AddReimbursement
          onSuccess={(data) => handleSuccess(data, "ADD")}
          open={!!addApplicationType}
          onClose={() => setAddApplicationType(undefined)}
        />
      )}
      {addApplicationType === ApplicationTypeEnum.DAILY_SUPPLIES && (
        <AddDailySupplies
          onSuccess={(data) => handleSuccess(data, "ADD")}
          open={!!addApplicationType}
          onClose={() => setAddApplicationType(undefined)}
        />
      )}
      {addApplicationType === ApplicationTypeEnum.MATERIAL_OVER_ISSUE && (
        <AddMaterialOverIssue
          onSuccess={(data) => handleSuccess(data, "ADD")}
          open={!!addApplicationType}
          onClose={() => setAddApplicationType(undefined)}
        />
      )}
      {detail &&
        (detail.type === ApplicationTypeEnum.MATERIAL_OVER_ISSUE ? (
          <DetailMaterialOverIssue
            detail={detail}
            open={!!detail}
            onClose={() => setDetail(null)}
            onSuccess={(data) => handleSuccess(data, "UPDATE")}
          />
        ) : (
          <DetailAndApprove
            detail={detail}
            open={!!detail}
            onClose={() => setDetail(null)}
            onSuccess={(data) => handleSuccess(data, "UPDATE")}
          />
        ))}
    </>
  );
};

export default ReimburseList;
